import React, { useState, useRef, useEffect, useCallback } from "react";
import { GoogleMap, StandaloneSearchBox, useJsApiLoader, Marker } from "@react-google-maps/api";
import { validateBusinessAddress } from "../utils/textQuality";

const libraries = ["places"]; // Required for StandaloneSearchBox

// Google's script normally loads in well under a second. If it has not loaded
// by the time this elapses we stop waiting and let the user type the address,
// rather than leaving them stuck on "Loading Google Maps...".
const MAPS_LOAD_TIMEOUT_MS = 8000;

// The key itself arrives asynchronously (a poll over `window.site`), so it is
// briefly absent on first render. Wait this long before treating that absence
// as "there is no key" and telling the user suggestions are unavailable.
const MAPS_KEY_WAIT_MS = 5000;

// Google's loader is a process-wide singleton that throws if it is ever
// constructed twice with different options, so `useJsApiLoader` must not run
// until the real key is in hand - calling it with the placeholder value and
// again with the key crashes the render. Isolating the hook here lets the
// parent hold it back without breaking rules-of-hooks or losing its own state.
const MapsScriptLoader = ({ apiKey, onStatusChange }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries,
    });

    useEffect(() => {
        onStatusChange({ isLoaded, loadError });
    }, [isLoaded, loadError, onStatusChange]);

    return null;
};

const GoogleMapComponent = ({ setSelectedPlace, setAddressData, formData, mapsAPIkey, mapVisible, onBlur, placeholder = "Search for a location" }) => {
    const inputRef = useRef(null); // Reference for input field
    const mapRef = useRef(null); // Reference for Google Map
    // const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default: New York
    const [mapCenter, setMapCenter] = useState({
        lat: 34.0522,
        lng: -118.2437
    }); // Default: Los Angeles
    const [zoom, setZoom] = useState(12);
    const [address, setAddress] = useState(null);
    const [count, setCount] = useState(0);
    const [manualEntry, setManualEntry] = useState(false);
    const [loadTimedOut, setLoadTimedOut] = useState(false);
    const [authFailed, setAuthFailed] = useState(false);
    const [manualError, setManualError] = useState("");
    const [keyWaitElapsed, setKeyWaitElapsed] = useState(false);
    const [{ isLoaded, loadError }, setLoaderStatus] = useState({
        isLoaded: false,
        loadError: undefined,
    });

    // Last address object handed to the parent. Parents wrap `setAddressData`
    // in their own callback, so it cannot be updated with a function the way a
    // plain useState setter can - we merge against this ref instead.
    const addressDataRef = useRef({});

    // Load Google Maps API. Rendered on every return path below so the script
    // is not abandoned mid-flight when the component drops into manual mode.
    const loaderElement = mapsAPIkey
        ? <MapsScriptLoader apiKey={mapsAPIkey} onStatusChange={setLoaderStatus} />
        : null;

    // Google invokes this global when the key is rejected (invalid key, wrong
    // referrer, billing disabled). The script still "loads", so this is the
    // only reliable signal for those failures.
    useEffect(() => {
        const previousHandler = window.gm_authFailure;
        window.gm_authFailure = () => {
            setAuthFailed(true);
            if (typeof previousHandler === "function") previousHandler();
        };
        return () => {
            window.gm_authFailure = previousHandler;
        };
    }, []);

    useEffect(() => {
        if (isLoaded || loadError || !mapsAPIkey) return undefined;
        const timer = setTimeout(() => setLoadTimedOut(true), MAPS_LOAD_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [isLoaded, loadError, mapsAPIkey]);

    useEffect(() => {
        if (mapsAPIkey) return undefined;
        const timer = setTimeout(() => setKeyWaitElapsed(true), MAPS_KEY_WAIT_MS);
        return () => clearTimeout(timer);
    }, [mapsAPIkey]);

    useEffect(() => {
        if (formData) {
            // setAddress(formData?.businessaddress)
            const lat = formData?.latitude ? formData?.latitude : 34.0522;
            const lng = formData?.longitude ? formData?.longitude : -118.2437;
            setMapCenter({
                lat, lng
            });
            // setCount(1)
        }


    }, [formData]);

    // Places is unusable: no key configured, the script failed or never
    // arrived, the key was rejected, or the places library is missing. A key
    // that has simply not arrived yet does not count - that is still pending.
    const placesUnavailable =
        (!mapsAPIkey && keyWaitElapsed) ||
        !!loadError ||
        loadTimedOut ||
        authFailed ||
        (isLoaded && !window.google?.maps?.places?.SearchBox);

    // Manual mode is also reachable on request, for when the script loads but
    // suggestions come back empty (quota exceeded, request denied). While the
    // key is still pending the same plain field is shown, minus the notice, so
    // the address can be typed right away and upgrades to Places on arrival.
    const showManualInput = placesUnavailable || manualEntry || !mapsAPIkey;

    const emitAddressData = useCallback((next, { merge = true } = {}) => {
        const value = merge ? { ...addressDataRef.current, ...next } : next;
        addressDataRef.current = value;
        setAddressData(value);
    }, [setAddressData]);

    const processAddressComponents = (addressComponents) => {
        const addressData = {};

        addressComponents?.forEach(component => {
            const types = component.types;

            if (types.includes('street_number')) {
                addressData.street_number = component.long_name;
            } else if (types.includes('route')) {
                addressData.route = component.long_name;
            } else if (types.includes('locality')) {
                addressData.city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
                addressData.state = component.long_name;
                addressData.stateShortName = component.short_name;
            } else if (types.includes('postal_code')) {
                addressData.postal_code = component.long_name;
            } else if (types.includes('sublocality_level_1')) {
                addressData.area = component.long_name;
            } else if (types.includes('administrative_area_level_2')) {
                addressData.county = component.long_name;
            }
        });

        return addressData;
    };



    // Convert Lat & Lng to Address (Reverse Geocoding)
    const getAddressFromLatLng = (lat, lng) => {
        const geocoder = new window.google.maps.Geocoder();
        const latLng = new window.google.maps.LatLng(lat, lng);

        geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === "OK" && results[0]) {

                // // console.log('resultts ', results[0])
                const fullAddressData = processAddressComponents(results[0].address_components);

                const formattedAddress = {
                    url: results[0].url,
                    geolocation: results[0].geometry,
                    zipcode: fullAddressData.postal_code || '',
                    state: fullAddressData.state || fullAddressData.city || '',
                    stateShortName: fullAddressData.stateShortName || '',
                    address: `${fullAddressData.street_number || ''} ${fullAddressData.route || ''} ${fullAddressData.area || ''}`.trim(),
                    city: fullAddressData.city || '',
                    area: fullAddressData.area || '',
                    fullAddress: results[0].formatted_address || '',
                    county: fullAddressData.county?.includes(" County")
                        ? fullAddressData.county.replace(" County", "")
                        : fullAddressData.county,

                };

                // // console.log('formattedAddress ', formattedAddress)
                setAddress(results[0].formatted_address);
                setManualError("");
                emitAddressData(formattedAddress, { merge: false });
                // setSelectedPlace(results[0].formatted_address);


            } else {
                console.error("Geocoder failed due to:", status);
            }
        });
    };

    // Function to handle place selection
    const handlePlacesChanged = () => {
        //  if (manualEntry) return;
        const places = inputRef.current.getPlaces();
        if (places?.length > 0) {
            const location = places?.[0]?.geometry.location;
            // console.log('places ', places, location.lat(), location.lng())
            setMapCenter({
                lat: location.lat(),
                lng: location.lng(),
            });
            setZoom(15);
            const fullAddressData = processAddressComponents(places?.[0]?.address_components);

            const formattedAddress = {
                url: places?.[0]?.url,
                geolocation: places?.[0]?.geometry,
                zipcode: fullAddressData.postal_code || '',
                state: fullAddressData.state || fullAddressData.city || '',
                stateShortName: fullAddressData.stateShortName || '',
                address: `${fullAddressData.street_number || ''} ${fullAddressData.route || ''} ${fullAddressData.area || ''}`.trim(),
                city: fullAddressData.city || '',
                area: fullAddressData.area || '',
                fullAddress: places?.[0]?.formatted_address || '',
                county: fullAddressData.county?.includes(" County")
                    ? fullAddressData.county.replace(" County", "")
                    : fullAddressData.county,

            };
            setAddress(places[0].formatted_address);
            setManualError("");
            emitAddressData(formattedAddress, { merge: false });
            // setSelectedPlace(places[0].formatted_address);

            // getAddressFromLatLng(location.lat(), location.lng()); // Get address


        }
    };

    const handleInputChange = (event) => {
        const value = event.target.value;
        // // console.log("value", value)
        setAddress(value);
        setManualError("");
        emitAddressData({ fullAddress: value });
    };

    // Typed address, with no place behind it: keep only the text and flag it so
    // callers can tell it was not resolved by Google.
    const handleManualChange = (event) => {
        const value = event.target.value;
        setAddress(value);
        setManualError("");
        emitAddressData(
            { fullAddress: value, address: value, manualEntry: true },
            { merge: false }
        );
    };

    const handleManualBlur = () => {
        const value = (address ?? formData?.businessaddress ?? "").trim();
        // Only complain once something has been typed - leaving the field
        // untouched is the form's own "required" check to report.
        setManualError(value ? validateBusinessAddress(value) : "");
        if (onBlur) onBlur();
    };

    const enableManualEntry = () => {
        setManualEntry(true);
        setManualError("");
    };


    // When marker is dragged to a new position
    const handleMarkerDragEnd = (event) => {
        // console.log('event ', event.latLng.lat(), event.latLng.lng())
        setMapCenter({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        });

        getAddressFromLatLng(event.latLng.lat(), event.latLng.lng());
    };

    const handleMapClick = (event) => {
        setMapCenter({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        });
        getAddressFromLatLng(event.latLng.lat(), event.latLng.lng());
    };

    const inputValue = address == null ? (formData?.businessaddress ?? "") : address;

    // Address suggestions are down - fall back to a plain text field so signup
    // is never blocked by Google being unavailable.
    if (showManualInput) {
        return (
            <div className="map-style manual-address-entry" style={{ width: "100%", flex: 1, minWidth: 0 }}>
                {loaderElement}
                <input
                    type="text"
                    id="locationSearchInput"
                    placeholder="Enter your full business address"
                    value={inputValue}
                    className="form-control"
                    onChange={handleManualChange}
                    onBlur={handleManualBlur}
                    style={{ width: "100%", height: "38px", padding: "10px" }}
                />
                {placesUnavailable && (
                    <small style={{ display: "block", marginTop: "4px", color: "#64748B" }} role="status">
                        Address suggestions are unavailable right now. Please type your full
                        address, including city, state and zip code.
                    </small>
                )}
                {manualError && (
                    <span className="errormsg" style={{ display: "block", marginTop: "4px", color: "red" }}>
                        {manualError}
                    </span>
                )}
            </div>
        );
    }

    // Ensure map loads only when isLoaded is true
    if (!isLoaded) {
        return <div>{loaderElement}Loading Google Maps...</div>;
    }


    // console.log(mapCenter, 'mapCenter',)
    // console.log('address ', address)
    // console.log(formData, 'formData',)
    const escapeHatchVisible = !mapVisible;

    return (
        <div className="map-style" style={{ width: "100%" }}>
            {loaderElement}
            {/* Search Box */}
            <StandaloneSearchBox
                onLoad={(ref) => (inputRef.current = ref)}
                onPlacesChanged={handlePlacesChanged}
            >
                <input
                    type="text"
                    id="locationSearchInput"

                    placeholder={placeholder}
                    value={inputValue}
                    className="form-control"
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    //  onKeyDown={handleKeyPress}
                    style={{
                        width: "91%",
                        height: "38px",
                        padding: "10px",
                        position: "absolute",
                        top: "0px",
                        left: "54%",
                        transform: "translateX(-50%)",
                        zIndex: 1000,
                        borderLeft: "none",
                        borderRadius: "0px 5px 5px 0px",
                        border: "1px solid #E2E8F0",
                    }}
                />

            </StandaloneSearchBox>


            {mapVisible &&
                <GoogleMap
                    mapContainerStyle={{ height: "400px", width: "100%" }}
                    center={mapCenter}
                    zoom={zoom}
                    onClick={handleMapClick}
                >
                    {/* <Marker position={mapCenter} /> */}
                    {/* Draggable Marker */}
                    <Marker
                        position={mapCenter}
                        draggable={true} // Allow dragging
                        onDragEnd={handleMarkerDragEnd} // Update position after drag
                    />
                </GoogleMap>}

            {/* Escape hatch for when the script loaded but suggestions come back
                empty (quota exceeded, request denied). Kept in normal flow so it
                owns the space it needs - the input above is absolutely positioned
                and this wrapper would otherwise collapse, letting whatever the
                parent renders next (a validation message, usually) land on top of
                it. Indented to the same edges as that input so the two line up. */}
            {escapeHatchVisible && (
                <button
                    type="button"
                    onClick={enableManualEntry}
                    style={{
                        display: "block",
                        marginLeft: "8.5%",
                        marginTop: "2px",
                        width: "91%",
                        textAlign: "left",
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: "12px",
                        color: "#64748B",
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                >
                    Can't find your address? Enter it manually
                </button>
            )}
        </div>
    );
};

export default GoogleMapComponent;
